import { useState } from "react";
import classNames from "classnames";
import { useScrollEdges } from "./useScrollEdges";
import styles from "./BoardScrollEdges.module.scss";

type ScrollEdge = "top" | "bottom" | "left" | "right";

export type ScrollEdgeAxis = "x" | "y" | "both";

/**
 * `blur` steps a backdrop blur up under the fade. Every blurred band redraws
 * each frame something animates beneath it, so edges repeated per list (one
 * pair per column) use `fade` alone.
 */
export type ScrollEdgeLook = "blur" | "fade";

const EDGES_BY_AXIS: Record<ScrollEdgeAxis, readonly ScrollEdge[]> = {
    x: ["left", "right"],
    y: ["top", "bottom"],
    both: ["top", "bottom", "left", "right"],
};

interface BoardScrollEdgesProps {
    scroller: HTMLElement | null;
    axis: ScrollEdgeAxis;
    look?: ScrollEdgeLook;
    /** Places the layer over the scroller and sets its insets. */
    className?: string;
}

/**
 * Faded (optionally blurred) bands over the edges of `scroller` past which
 * content continues. The consumer lays it over the scroller (same grid area);
 * `--board-scroll-edges-inset-top` clears sticky headers inside it.
 */
export const BoardScrollEdges = ({
    scroller,
    axis,
    look = "blur",
    className,
}: Readonly<BoardScrollEdgesProps>) => {
    const [layer, setLayer] = useState<HTMLElement | null>(null);
    useScrollEdges(scroller, layer);
    return (
        <div
            ref={setLayer}
            aria-hidden
            data-look={look}
            className={classNames(styles.edges, className)}
        >
            {EDGES_BY_AXIS[axis].map((edge) => (
                <div key={edge} data-edge={edge} className={styles.edge} />
            ))}
        </div>
    );
};
